<template>
  <div class="printer-operators-container">
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <h2>打单员管理</h2>
          <div class="header-actions">
            <el-button type="primary" @click="showAddDialog">
              <el-icon><Plus /></el-icon>
              新增打单员
            </el-button>
            <el-button type="warning" @click="showSyncConfirm" :loading="syncLoading">
              <el-icon><RefreshRight /></el-icon>
              同步打单员
            </el-button>
          </div>
        </div>
      </template>
      
      <div class="card-filter">
        <el-form :inline="true" :model="searchForm" label-position="left">
          <el-form-item label="手机号搜索">
            <el-input
              v-model="searchForm.keyword"
              placeholder="请输入手机号"
              clearable
              @keyup.enter="handleSearch"
            >
              <template #append>
                <el-button @click="handleSearch">
                  <el-icon><Search /></el-icon>
                </el-button>
              </template>
            </el-input>
          </el-form-item>
          
          <el-form-item>
            <el-button type="default" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
      </div>
      
      <div class="card-table">
        <el-table
          v-loading="loading"
          :data="printerOperators"
          border
          style="width: 100%"
          stripe
          size="medium"
        >
          <el-table-column prop="name" label="姓名" align="center"></el-table-column>
          <el-table-column prop="phone" label="手机号" width="160" align="center"></el-table-column>
          <el-table-column prop="is_bound" label="绑定状态" width="120" align="center">
            <template #default="scope">
              <el-tag :type="scope.row.is_bound ? 'success' : 'warning'" size="small">
                {{ scope.row.is_bound ? '已绑定' : '未绑定' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" align="center">
            <template #default="scope">
              <el-button
                v-if="!scope.row.is_bound"
                type="primary"
                size="small"
                @click="handleBind(scope.row)"
              >
                绑定
              </el-button>
              <el-button
                v-else
                type="danger"
                size="small"
                @click="handleUnbind(scope.row)"
              >
                解绑
              </el-button>
              <el-button
                type="danger"
                size="small"
                @click="handleDelete(scope.row)"
              >
                <el-icon><Delete /></el-icon>
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      
      <div class="card-footer">
        <el-pagination
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
    
    <!-- 绑定对话框 -->
    <el-dialog
      v-model="bindDialogVisible"
      title="绑定打单员"
      width="400px"
      :close-on-click-modal="false"
    >
      <el-form :model="bindForm" :rules="bindRules" ref="bindFormRef" label-width="80px">
        <el-form-item label="打单员" prop="name">
          <el-input v-model="bindForm.name" disabled />
        </el-form-item>
        <el-form-item label="标签ID列表" prop="tagid_list">
          <el-input
            v-model="bindForm.tagid_list"
            placeholder="多个标签ID用逗号分隔，例如：123,456"
            type="textarea"
            :rows="3"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="bindDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmBind" :loading="bindLoading">确定绑定</el-button>
        </span>
      </template>
    </el-dialog>
    
    <!-- 新增打单员对话框 -->
    <el-dialog
      v-model="addDialogVisible"
      title="新增打单员"
      width="700px"
      center
    >
      <div class="add-printer-dialog">
        <!-- 会员搜索 -->
        <el-form :inline="true" :model="addPrinterSearchParams" label-position="left" class="mb-4">
          <el-form-item label="会员手机号">
            <el-input
              v-model="addPrinterSearchParams.phone"
              placeholder="请输入会员手机号"
              clearable
              @keyup.enter="fetchAvailableMembers"
            >
              <template #append>
                <el-button @click="fetchAvailableMembers">
                  <el-icon><Search /></el-icon>
                </el-button>
              </template>
            </el-input>
          </el-form-item>
        </el-form>
        
        <!-- 会员列表 -->
        <el-table
          v-loading="memberLoading"
          :data="availableMembers"
          border
          style="width: 100%"
          @row-click="handleMemberSelect"
          row-key="id"
          ref="memberTableRef"
          stripe
          size="medium"
        >
          <el-table-column type="selection" width="55" />
          <el-table-column prop="name" label="姓名" width="100" align="center" />
          <el-table-column prop="phone" label="手机号" width="160" align="center" />
          <el-table-column prop="member_type_name" label="会员类型" width="140" align="center" />
        </el-table>
        
        <!-- 会员列表分页 -->
        <div class="dialog-footer mt-4">
          <el-pagination
            :current-page="memberCurrentPage"
            :page-size="memberPageSize"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="memberTotal"
            @size-change="handleMemberSizeChange"
            @current-change="handleMemberCurrentChange"
          />
        </div>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="addDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmAdd" :loading="addLoading">确定新增</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Search, Plus, Delete, RefreshRight } from '@element-plus/icons-vue';
import { getPrinterOperators, bindPrinterOperator, unbindPrinterOperator } from '@/api/printerOperators';
import service from '@/api/axios';

// 状态管理
const loading = ref(false);
const syncLoading = ref(false);
const printerOperators = ref([]);
const searchForm = reactive({ keyword: '' });

// 分页数据
const currentPage = ref(1);
const pageSize = ref(10);
const total = ref(0);

// 绑定对话框
const bindDialogVisible = ref(false);
const bindLoading = ref(false);
const bindFormRef = ref();
const bindForm = reactive({
  id: null,
  name: '',
  tagid_list: '',
});

// 新增对话框
const addDialogVisible = ref(false);
const addLoading = ref(false);
const memberTableRef = ref(null);

// 新增搜索参数
const addPrinterSearchParams = ref({
  phone: ''
});

// 可用会员列表
const availableMembers = ref([]);
const memberLoading = ref(false);
const memberCurrentPage = ref(1);
const memberPageSize = ref(10);
const memberTotal = ref(0);

// 表单验证规则
const bindRules = {
  tagid_list: [
    {
      pattern: /^$|^\d+(,\d+)*$/, 
      message: '标签ID列表格式错误，应为逗号分隔的数字', 
      trigger: 'blur'
    }
  ]
};

// 初始化加载数据
onMounted(() => {
  fetchPrinterOperators();
});

// 获取打单员列表
const fetchPrinterOperators = async () => {
  loading.value = true;
  try {
    const response = await getPrinterOperators({
      ...searchForm,
      page: currentPage.value,
      pageSize: pageSize.value
    });
    printerOperators.value = response.data || [];
    total.value = response.total || 0;
  } catch (error) {
    ElMessage.error('获取打单员列表失败：' + (error.message || '未知错误'));
    printerOperators.value = [];
    total.value = 0;
  } finally {
    loading.value = false;
  }
};

// 搜索
const handleSearch = () => {
  currentPage.value = 1;
  fetchPrinterOperators();
};

// 重置搜索
const resetSearch = () => {
  searchForm.keyword = '';
  currentPage.value = 1;
  fetchPrinterOperators();
};

// 分页大小变化
const handleSizeChange = (size) => {
  pageSize.value = size;
  fetchPrinterOperators();
};

// 当前页变化
const handleCurrentChange = (page) => {
  currentPage.value = page;
  fetchPrinterOperators();
};

// 显示新增对话框
const showAddDialog = () => {
  addDialogVisible.value = true;
  // 重置搜索条件
  addPrinterSearchParams.value = {
    phone: ''
  };
  // 重置分页
  memberCurrentPage.value = 1;
  memberPageSize.value = 10;
  // 获取可用会员列表
  fetchAvailableMembers();
};

// 获取可用会员列表
const fetchAvailableMembers = async () => {
  memberLoading.value = true;
  try {
    const response = await service({
      url: '/manage/member/list',
      method: 'get',
      params: {
        page: memberCurrentPage.value,
        pageSize: memberPageSize.value,
        keyword: addPrinterSearchParams.value.phone
      }
    });
    
    // 确保availableMembers始终是数组
    availableMembers.value = Array.isArray(response.data) ? response.data : [];
    memberTotal.value = response.total || 0;
  } catch (error) {
    ElMessage.error('获取会员列表失败：' + (error.message || '未知错误'));
    console.error('获取会员列表失败:', error);
    // 出错时确保availableMembers是数组
    availableMembers.value = [];
    memberTotal.value = 0;
  } finally {
    memberLoading.value = false;
  }
};

// 处理会员行点击
const handleMemberSelect = (row) => {
  if (memberTableRef.value) {
    memberTableRef.value.toggleRowSelection(row);
  }
};

// 处理会员分页大小变化
const handleMemberSizeChange = (size) => {
  memberPageSize.value = size;
  memberCurrentPage.value = 1;
  fetchAvailableMembers();
};

// 处理会员当前页变化
const handleMemberCurrentChange = (page) => {
  memberCurrentPage.value = page;
  fetchAvailableMembers();
};

// 确认新增
const confirmAdd = async () => {
  if (!memberTableRef.value) return;
  
  // 获取选中的会员
  const selectedMembers = memberTableRef.value.getSelectionRows();
  if (selectedMembers.length === 0) {
    ElMessage.warning('请选择要添加的会员');
    return;
  }
  
  addLoading.value = true;
  try {
    // 批量添加打单员
    for (const member of selectedMembers) {
      await service({
        url: '/manage/printer-operators',
        method: 'post',
        data: {
          user_id: member.id
        }
      });
    }
    
    ElMessage.success('新增打单员成功');
    // 关闭弹窗
    addDialogVisible.value = false;
    // 刷新打单员列表
    fetchPrinterOperators();
  } catch (error) {
    ElMessage.error('新增打单员失败：' + (error.message || '未知错误'));
    console.error('新增打单员失败:', error);
  } finally {
    addLoading.value = false;
  }
};

// 处理绑定
const handleBind = (row) => {
  bindForm.id = row.id;
  bindForm.name = row.name;
  bindForm.tagid_list = row.tagid_list || '';
  bindDialogVisible.value = true;
};

// 确认绑定
const confirmBind = async () => {
  if (!bindFormRef.value) return;
  
  await bindFormRef.value.validate(async (valid) => {
    if (valid) {
      bindLoading.value = true;
      try {
        await bindPrinterOperator(bindForm.id, {
          tagid_list: bindForm.tagid_list
        });
        ElMessage.success('绑定成功');
        bindDialogVisible.value = false;
        fetchPrinterOperators();
      } catch (error) {
        ElMessage.error('绑定失败：' + (error.message || '未知错误'));
      } finally {
        bindLoading.value = false;
      }
    }
  });
};

// 处理解绑
const handleUnbind = async (row) => {
  await ElMessageBox.confirm('确定要解绑该打单员吗？', '解绑确认', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning',
  }).then(async () => {
    try {
      await unbindPrinterOperator(row.id);
      ElMessage.success('解绑成功');
      fetchPrinterOperators();
    } catch (error) {
      ElMessage.error('解绑失败：' + (error.message || '未知错误'));
    }
  }).catch(() => {
    // 取消操作
  });
};

// 处理删除打单员
const handleDelete = async (printerOperator) => {
  try {
    // 检查打单员是否已绑定
    if (printerOperator.is_bound) {
      ElMessage.warning('已绑定的打单员不允许删除，请先解绑');
      return;
    }
    
    // 显示确认对话框
    await ElMessageBox.confirm(
      `确定要删除打单员 "${printerOperator.name}" 吗？`,
      '删除确认',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }
    );
    
    // 调用删除API
    await service({
      url: `/manage/printer-operators/${printerOperator.id}`,
      method: 'delete'
    });
    
    ElMessage.success('删除打单员成功');
    // 刷新打单员列表
    fetchPrinterOperators();
  } catch (error) {
    if (error === 'cancel') {
      // 用户取消删除操作，不显示错误提示
      return;
    }
    ElMessage.error('删除打单员失败：' + (error.message || '未知错误'));
    console.error('删除打单员失败:', error);
  }
};

// 显示同步确认对话框
const showSyncConfirm = () => {
  ElMessageBox.confirm(
    '此操作将从微信后台获取最新打单员列表，并完全替换当前系统中的打单员数据。该操作不可撤销，是否继续？',
    '同步确认',
    {
      confirmButtonText: '确认',
      cancelButtonText: '取消',
      type: 'warning',
    }
  ).then(async () => {
    await syncPrinterOperators();
  }).catch(() => {
    // 用户取消操作
  });
};

// 同步打单员
const syncPrinterOperators = async () => {
  syncLoading.value = true;
  try {
    // 调用同步API
    await service({
      url: '/manage/printer-operators/sync',
      method: 'post'
    });
    
    ElMessage.success('打单员列表已成功同步');
    // 刷新打单员列表
    fetchPrinterOperators();
  } catch (error) {
    ElMessage.error('同步打单员失败：' + (error.message || '未知错误'));
    console.error('同步打单员失败:', error);
  } finally {
    syncLoading.value = false;
  }
};
</script>

<style scoped>
.printer-operators-container {
  padding: 20px;
}

.page-title {
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.card-header h2 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.card-filter {
  margin-bottom: 20px;
  padding: 10px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.card-table {
  margin-bottom: 20px;
}

.card-footer {
  display: flex;
  justify-content: flex-end;
  padding: 10px 0;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.mb-4 {
  margin-bottom: 16px;
}

.mt-4 {
  margin-top: 16px;
}
</style>